import type { LocalTrack } from "@trip/contracts/bridge";
import { useEffect, useState } from "react";
import { nativeBridge, type Bridge } from "./bridge.ts";

const EMPTY: LocalTrack = { here: null, pending: [], zone: null };

/**
 * Where this phone is and the trail it has not pushed yet, straight from the recorder rather than
 * from the server. Asks once, then follows `location.moved`, so the marker and the trail keep up
 * with the walk instead of jumping forward once a batch lands.
 *
 * Null in a browser: there is no recorder there, and the bridge is the only source of coordinates.
 */
export function useLocalTrack(bridge: Bridge | null = nativeBridge()): LocalTrack | null {
  const [track, setTrack] = useState<LocalTrack | null>(null);

  useEffect(() => {
    if (!bridge) return;
    let live = true;
    void bridge
      .call("location.here", {})
      .then((t) => {
        if (live) setTrack(t ?? EMPTY);
      })
      .catch(() => {
        // An older shell without the call: the map simply has no local trail to draw.
        if (live) setTrack(null);
      });
    const off = bridge.on("location.moved", (t) => {
      if (live) setTrack(t);
    });
    return () => {
      live = false;
      void off();
    };
  }, [bridge]);

  return track;
}
